import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import es from '../i18n/es';
import en from '../i18n/en';

const translations = { es, en };
const SUPPORTED = ['es', 'en'];
const STORAGE_KEY = 'ofimex_locale';

function detectInitialLocale() {
  // 1) explicit user choice persisted in localStorage
  const stored = typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED.includes(stored)) return stored;

  // 2) user's saved preference from their profile (set by the backend)
  try {
    const userJson = localStorage.getItem('ofimex_user');
    if (userJson) {
      const userLang = JSON.parse(userJson).language;
      if (userLang && SUPPORTED.includes(userLang)) return userLang;
    }
  } catch { /* ignore */ }

  // 3) browser language (e.g. "en-US" → "en")
  if (typeof navigator !== 'undefined' && navigator.language) {
    const lang = navigator.language.toLowerCase().split('-')[0];
    if (SUPPORTED.includes(lang)) return lang;
  }

  // 4) fallback
  return 'es';
}

const I18nContext = createContext();

export function I18nProvider({ children }) {
  const [locale, setLocale] = useState(detectInitialLocale);

  // Apply <html lang="..."> so screen readers / browsers reflect the active locale
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const t = useCallback((key, vars) => {
    const keys = key.split('.');
    let value = translations[locale];
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key;
      }
    }
    if (typeof value !== 'string') return key;
    if (vars && typeof vars === 'object') {
      return value.replace(/\{(\w+)\}/g, (m, name) => (vars[name] !== undefined ? String(vars[name]) : m));
    }
    return value;
  }, [locale]);

  // Update token key references to match the rest of the app (`ofimex_token`)
  const changeLocale = useCallback((newLocale) => {
    if (translations[newLocale]) {
      setLocale(newLocale);
      localStorage.setItem(STORAGE_KEY, newLocale);
      // Best-effort persist on the server (only if user is logged in). Fire-and-forget.
      try {
        const token = localStorage.getItem('ofimex_token');
        if (token) {
          const url = (process.env.REACT_APP_BACKEND_URL || '') + '/api/auth/me/language';
          fetch(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ language: newLocale }),
          }).catch(() => {});
        }
      } catch { /* ignore */ }
    }
  }, []);

  return (
    <I18nContext.Provider value={{ locale, t, changeLocale }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}
